'use client';

import { useEffect, useMemo, useRef } from 'react';

import { useTexture } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

import { fragmentShader } from './shaders/fragment';
import { vertexShader } from './shaders/vertex';
import { simplex2D } from './utils/noise';

export interface ParticleImageProps {
  src: string;
  density?: number;
  size?: number;
  blackCutoff?: number;
  fadeInDuration?: number;
  lerpStrength?: number;
  repelRadius?: number;
  repelStrength?: number;
  returnStrength?: number;
  friction?: number;
  interactionEnabled?: boolean;
  noiseEnabled?: boolean;
  noiseScale?: number;
  noiseSpeed?: number;
  noiseAmplitude?: number;
  brightnessFlickerEnabled?: boolean;
  brightnessFlickerScale?: number;
  brightnessFlickerSpeed?: number;
  brightnessFlickerAmount?: number;
  grayscale?: boolean;
  contrast?: number;
  brightness?: number;
  ditherEnabled?: boolean;
  ditherScale?: number;
}

const ParticleImage = ({
  src,
  density = 150,
  size: particleSize = 4.0,
  blackCutoff = 0.1,
  fadeInDuration = 0.8,
  lerpStrength = 0.4,
  repelRadius = 1.5,
  repelStrength = 0.01,
  returnStrength = 0.05,
  friction = 0.8,
  interactionEnabled = true,
  noiseEnabled = true,
  noiseScale = 4.0,
  noiseSpeed = 0.2,
  noiseAmplitude = 0.001,
  brightnessFlickerEnabled = true,
  brightnessFlickerScale = 5.0,
  brightnessFlickerSpeed = 0.3,
  brightnessFlickerAmount = 1.0,
  grayscale = true,
  contrast = 1.2,
  brightness = 1.0,
  ditherEnabled = true,
  ditherScale = 1.0,
}: ParticleImageProps) => {
  const texture = useTexture(src);
  const { gl, viewport, size } = useThree();
  const isHoveringRef = useRef(false);
  const materialRef = useRef<THREE.ShaderMaterial>(null);
  const geometryRef = useRef<THREE.BufferGeometry>(null);
  const opacityRef = useRef(0);
  const flickerTimeRef = useRef(0);
  const noiseTimeRef = useRef(0);

  const positionsRef = useRef<Float32Array | null>(null);
  const originalPositionsRef = useRef<Float32Array | null>(null);
  const velocitiesRef = useRef<Float32Array | null>(null);
  const particleCountRef = useRef(0);

  const mouseRef = useRef({ x: 9999, y: 9999 });
  const targetMouseRef = useRef({ x: 9999, y: 9999 });

  const { width, height } = texture.image as HTMLImageElement;
  const aspect = width / height;

  // Object-fit: cover logic
  const imageAspect = width / height;
  const viewportAspect = viewport.width / viewport.height;

  let planeWidth: number;
  let planeHeight: number;

  if (imageAspect > viewportAspect) {
    planeHeight = viewport.height;
    planeWidth = planeHeight * imageAspect;
  } else {
    planeWidth = viewport.width;
    planeHeight = planeWidth / imageAspect;
  }

  const offsetX = (planeWidth - viewport.width) / 2;
  const offsetY = (planeHeight - viewport.height) / 2;

  const segmentsX = Math.floor(density * aspect);
  const segmentsY = density;

  const { positions, originalPositions, uvs, count } = useMemo(() => {
    const particleCount = (segmentsX + 1) * (segmentsY + 1);

    const tempPositions = new Float32Array(particleCount * 3);
    const tempOriginalPositions = new Float32Array(particleCount * 3);
    const tempUvs = new Float32Array(particleCount * 2);

    let index = 0;

    for (let iy = 0; iy <= segmentsY; iy += 1) {
      const v = iy / segmentsY;
      const y = v * planeHeight - planeHeight / 2 - offsetY;

      for (let ix = 0; ix <= segmentsX; ix += 1) {
        const u = ix / segmentsX;
        const x = u * planeWidth - planeWidth / 2 - offsetX;

        tempPositions[index * 3] = x;
        tempPositions[index * 3 + 1] = y;
        tempPositions[index * 3 + 2] = 0;

        tempOriginalPositions[index * 3] = x;
        tempOriginalPositions[index * 3 + 1] = y;
        tempOriginalPositions[index * 3 + 2] = 0;

        tempUvs[index * 2] = u;
        tempUvs[index * 2 + 1] = v;

        index += 1;
      }
    }

    return { positions: tempPositions, originalPositions: tempOriginalPositions, uvs: tempUvs, count: particleCount };
  }, [segmentsX, segmentsY, planeWidth, planeHeight, offsetX, offsetY]);

  // Initialize physics state and geometry attributes imperatively
  useEffect(() => {
    // Optimize: use direct Float32Array instead of slice
    positionsRef.current = new Float32Array(positions);
    originalPositionsRef.current = new Float32Array(originalPositions);
    velocitiesRef.current = new Float32Array(count * 3);
    particleCountRef.current = count;

    // Set up geometry attributes imperatively for better control
    if (!geometryRef.current) {
      return undefined;
    }

    const geometry = geometryRef.current;

    // Create position attribute with the ref array
    const positionAttribute = new THREE.BufferAttribute(positionsRef.current, 3);

    geometry.setAttribute('position', positionAttribute);

    // Create UV attribute (static)
    const uvAttribute = new THREE.BufferAttribute(uvs, 2);

    geometry.setAttribute('aOriginalUv', uvAttribute);

    return () => {
      geometry.deleteAttribute('position');
      geometry.deleteAttribute('aOriginalUv');
    };
  }, [positions, originalPositions, uvs, count]);

  const uniforms = useMemo(
    () => ({
      uTexture: { value: texture },
      uPointSize: { value: particleSize },
      uPixelRatio: { value: gl.getPixelRatio() },
      uBlackCutoff: { value: blackCutoff },
      uOpacity: { value: 0 },
      uTime: { value: 0 },
      uBrightnessFlickerScale: { value: brightnessFlickerScale },
      uBrightnessFlickerAmount: { value: brightnessFlickerEnabled ? brightnessFlickerAmount : 0 },
      uGrayscale: { value: grayscale ? 1.0 : 0.0 },
      uContrast: { value: contrast },
      uBrightness: { value: brightness },
      uDitherEnabled: { value: ditherEnabled ? 1.0 : 0.0 },
      uDitherScale: { value: ditherScale },
    }),
    [
      texture,
      particleSize,
      gl,
      blackCutoff,
      brightnessFlickerScale,
      brightnessFlickerEnabled,
      brightnessFlickerAmount,
      grayscale,
      contrast,
      brightness,
      ditherEnabled,
      ditherScale,
    ]
  );

  // Mouse handling
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!size) return;

      const canvas = gl.domElement;
      const rect = canvas.getBoundingClientRect();

      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      targetMouseRef.current.x = (x * viewport.width) / 2;
      targetMouseRef.current.y = (y * viewport.height) / 2;
      isHoveringRef.current = true;
    };

    const handleMouseLeave = () => {
      isHoveringRef.current = false;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [size, gl, viewport]);

  // Physics update loop
  useFrame((state, delta) => {
    const positionsData = positionsRef.current;
    const originalPositionsData = originalPositionsRef.current;
    const velocities = velocitiesRef.current;
    const geometry = geometryRef.current;

    if (!positionsData || !originalPositionsData || !velocities || !geometry) return;

    // Update time for opacity flickering shader
    flickerTimeRef.current += delta * brightnessFlickerSpeed;

    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = flickerTimeRef.current;
    }

    // Update time for noise displacement
    noiseTimeRef.current += delta * noiseSpeed;

    // Animate opacity fade-in
    if (materialRef.current && opacityRef.current < 1) {
      opacityRef.current = Math.min(1, opacityRef.current + delta / fadeInDuration);
      materialRef.current.uniforms.uOpacity.value = opacityRef.current;
    }

    // Skip physics on mobile or if interaction disabled
    const isMobile = typeof window !== 'undefined' && 'ontouchstart' in window;

    if (!interactionEnabled || isMobile) {
      return;
    }

    // Smooth mouse lerp
    mouseRef.current.x += (targetMouseRef.current.x - mouseRef.current.x) * lerpStrength;
    mouseRef.current.y += (targetMouseRef.current.y - mouseRef.current.y) * lerpStrength;

    const mouseX = mouseRef.current.x;
    const mouseY = mouseRef.current.y;
    const repelRadiusSq = repelRadius * repelRadius;

    // Physics loop
    for (let i = 0; i < particleCountRef.current; i += 1) {
      const idx = i * 3;

      let x = positionsData[idx];
      let y = positionsData[idx + 1];
      let vx = velocities[idx];
      let vy = velocities[idx + 1];

      const ox = originalPositionsData[idx];
      const oy = originalPositionsData[idx + 1];

      // Organic noise displacement
      if (noiseEnabled) {
        const noiseX = simplex2D(ox * noiseScale, oy * noiseScale + noiseTimeRef.current);
        const noiseY = simplex2D(ox * noiseScale + 100, oy * noiseScale + noiseTimeRef.current);

        vx += noiseX * noiseAmplitude;
        vy += noiseY * noiseAmplitude;
      }

      // Mouse repel force
      const dx = x - mouseX;
      const dy = y - mouseY;
      const distSq = dx * dx + dy * dy;

      if (distSq < repelRadiusSq && distSq > 0.01) {
        const dist = Math.sqrt(distSq);
        const force = (1 - dist / repelRadius) * repelStrength;
        const nx = dx / dist;
        const ny = dy / dist;

        vx += nx * force;
        vy += ny * force;
      }

      // Spring force (return to original position)
      const returnDx = ox - x;
      const returnDy = oy - y;

      vx += returnDx * returnStrength;
      vy += returnDy * returnStrength;

      // Apply friction
      vx *= friction;
      vy *= friction;

      // Update position
      x += vx;
      y += vy;

      // Store updated values
      positionsData[idx] = x;
      positionsData[idx + 1] = y;
      velocities[idx] = vx;
      velocities[idx + 1] = vy;
    }

    // Upload updated positions to GPU
    const positionAttribute = geometry.attributes.position as THREE.BufferAttribute;

    positionAttribute.needsUpdate = true;
  });

  return (
    <points>
      <bufferGeometry ref={geometryRef} />
      {/* eslint-disable react/no-unknown-property */}
      <shaderMaterial
        ref={materialRef}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        uniforms={uniforms}
        transparent
        depthWrite={false}
      />
      {/* eslint-enable react/no-unknown-property */}
    </points>
  );
};

export default ParticleImage;
